import React from 'react';
import { styled } from '@mui/material/styles';
import { Link } from 'react-router-dom';
import { HashLink } from 'react-router-hash-link';
import { useLocation } from 'react-router';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import Avatar from '@mui/material/Avatar';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';

import { ListItemIcon } from '@mui/material';
import { CheckOutlined, LockOutlined, Circle } from '@mui/icons-material';
import { CompanySvg, FinanceSvg, SustainabilitySvg } from 'images';

const PREFIX = 'SidebarItem';

const classes = {
  root: `${PREFIX}-root`,
  selected: `${PREFIX}-selected`,
  avatar: `${PREFIX}-avatar`,
  nested: `${PREFIX}-nested`,
};

const Root = styled('div')(({ theme }) => ({
  [`& .${classes.root}`]: {},

  [`& .${classes.selected}`]: {
    '&.MuiListItem-root.Mui-selected': {
      backgroundColor: '#777',
    },
  },

  [`& .${classes.avatar}`]: {
    width: 30,
    height: 30,
    background: 'transparent',
  },

  [`& .${classes.nested}`]: {
    color: 'white',
    whiteSpace: 'break-spaces',
    fontSize: 12,
    paddingLeft: theme.spacing(4),
  },
}));

interface SectionMenuItem {
  path: string;
  label: string;
  lock: boolean;
  color: string;
}

interface IProps {
  label: string;
  icon?: string;
  subItems?: SectionMenuItem[];
  path?: string;
  disabled?: boolean;
  minimise: boolean;
}

const scrollWithOffset = (el: HTMLElement) => {
  const yCoordinate: number = el.getBoundingClientRect().top + window.pageYOffset;
  const yOffset = -80;
  window.scrollTo({ top: yCoordinate + yOffset, behavior: 'smooth' });
};

function getIconByName(icon: string, color: string): JSX.Element {
  switch (icon) {
    case 'CompanySvg':
      return <CompanySvg fill={color} stroke={color} />;
    case 'FinanceSvg':
      return <FinanceSvg fill={color} stroke={color} />;
    case 'SustainabilitySvg':
      return <SustainabilitySvg fill={color} stroke={color} />;
    default:
      return <Circle sx={{ color: 'white', fontSize: 12 }} />;
  }
}

function SidebarItem(props: IProps): JSX.Element {
  const location = useLocation<History>();
  const { pathname } = location;
  const { label, icon, subItems, path, minimise } = props;
  const [openSub, setOpenSub] = React.useState(true);
  const l = 50;
  const p = label.length > l ? label.substring(0, l) + '...' : label;
  const s = label.length > l ? `${label.substring(l)}` : '';
  const selected = path ? pathname?.endsWith(path) : false;

  const handleToggleOpenSubMenu = () => {
    setOpenSub(!openSub);
  };

  const listItemProps = {
    component: HashLink,
    scroll: scrollWithOffset,
    to: path ?? '/#',
  };

  return (
    <Root>
      <ListItem
        button
        classes={{
          root: classes.root,
          selected: classes.selected,
        }}
        selected={!!selected}
        {...listItemProps}
      >
        <ListItemAvatar sx={{ minWidth: 40 }}>
          <Avatar variant="rounded" className={classes.avatar}>
            {getIconByName(icon ?? '', selected ? 'white' : 'grey')}
          </Avatar>
        </ListItemAvatar>
        <ListItemText primary={p} secondary={s ? s : null} />
        {subItems && !minimise && (
          <ListItemSecondaryAction onClick={handleToggleOpenSubMenu}>
            <IconButton edge="end" color="inherit" aria-label="delete" size="large">
              {openSub ? <ExpandLess color="inherit" /> : <ExpandMore color="inherit" />}
            </IconButton>
          </ListItemSecondaryAction>
        )}
      </ListItem>
      {subItems && !minimise && (
        <Collapse in={openSub} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {subItems.map(({ label, path, color, lock }) => (
              <ListItem
                classes={{
                  root: classes.nested,
                  selected: classes.selected,
                }}
                selected={!!pathname.endsWith(path)}
                key={label}
                button
                component={Link}
                disabled={lock}
                to={path ?? '/#'}
              >
                <ListItemIcon>
                  {lock ? (
                    <LockOutlined style={{ color: 'white' }} />
                  ) : (
                    <CheckOutlined style={{ color: color }} />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={label}
                  primaryTypographyProps={{
                    variant: 'body2',
                  }}
                />
              </ListItem>
            ))}
          </List>
        </Collapse>
      )}
    </Root>
  );
}

export default SidebarItem;
